/**
 * SignalElement Base Class
 *
 * A LitElement subclass with SignalWatcher mixin that automatically
 * re-renders when any accessed signal values change.
 *
 * Use this as the base class for components that use @inject decorator
 * to access reactive state from the DI container.
 */

import { LitElement } from "lit";
import { SignalWatcher } from "@lit-labs/signals";

/**
 * Base class for components using signal-based dependency injection.
 * Combines LitElement with SignalWatcher for automatic re-renders.
 *
 * @example
 * import { SignalElement, inject, type InjectType } from "ui-common/di";
 * import { html } from "lit";
 * import { customElement } from "lit/decorators.js";
 *
 * @customElement("my-counter")
 * class MyCounter extends SignalElement {
 *   @inject("counterState")
 *   declare counterState: InjectType<"counterState">;
 *
 *   @inject("counterService")
 *   declare counterService: InjectType<"counterService">;
 *
 *   override render() {
 *     // Accessing signal properties automatically tracks dependencies
 *     // Component re-renders when counterState.count changes
 *     return html`
 *       <p>Count: ${this.counterState.count}</p>
 *       <button @click=${() => this.counterService.increment()}>+</button>
 *     `;
 *   }
 * }
 */
export const SignalElement = SignalWatcher(LitElement);

/**
 * Type alias for SignalElement instance.
 * Useful for typing parameters that accept SignalElement instances.
 */
export type SignalElementClass = InstanceType<typeof SignalElement>;
